import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  api,
  useGetPipelineRunQuery,
  useLazyGetRunTracesQuery,
  useListStagesQuery,
  useSearchEventsQuery
} from '@/services/api';
import { useUrlState } from '@/hooks/useUrlState';
import { useEventStream } from '@/hooks/useEventStream';
import { Chip } from '@/components/Chip';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonGridRow } from '@/components/Skeleton';
import { VirtualLogList } from '@/components/VirtualLogList';
import { ProgressTracker } from '@/components/ProgressTracker';
import { StageOutputsTab } from '@/components/stage-output/StageOutputsTab';
import { DownloadRequirementButton } from '@/components/DownloadRequirementButton';
import { CostTab } from '@/components/CostTab';
import { ErrorsTab } from '@/components/ErrorsTab';
import { ApprovalPanel } from '@/components/ApprovalPanel';
import {
  ALL_STAGES,
  LiveActivityPanel,
  type LiveActivityRow
} from '@/components/LiveActivityPanel';
import { StopRunButton } from '@/components/StopRunButton';
import { formatTimestamp, formatDuration } from '@/utils/format';
import { isLiveRunState } from '@/utils/runState';
import { foldTraceRows, mergeActivityRows } from '@/utils/traceRows';
import type {
  BackEdgeInfo,
  PipelineRunEventFrame,
  RunTraceRow,
  StageSubProgress
} from '@/types/api';

const URL_KEYS = ['tab', 'q', 'stage', 'panel', 'astage'] as const;

type TabKey = 'logs' | 'stage_outputs' | 'cost' | 'errors';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'logs', label: 'Logs' },
  { key: 'stage_outputs', label: 'Stage outputs' },
  { key: 'cost', label: 'Cost' },
  { key: 'errors', label: 'Errors' }
];

export function RunDetailPage(): JSX.Element {
  const params = useParams<{ runId: string }>();
  const runId = params.runId ?? '';
  const dispatch = useDispatch();
  const { values, set } = useUrlState(URL_KEYS);
  const tab = (values.tab as TabKey | undefined) ?? 'logs';
  const searchQuery = values.q ?? '';

  const POLL_MS = 4000;
  const APPROVAL_POLL_MS = 30000;
  const runStateForPoll = useGetPipelineRunQuery(runId, { skip: !runId }).data?.state;
  const runResp = useGetPipelineRunQuery(runId, {
    skip: !runId,
    skipPollingIfUnfocused: true,
    pollingInterval:
      runStateForPoll === 'paused_for_approval'
        ? APPROVAL_POLL_MS
        : isLiveRunState(runStateForPoll)
          ? POLL_MS
          : 0
  });
  const isAwaitingApproval = runResp.data?.state === 'paused_for_approval';
  const runRefreshing = isLiveRunState(runResp.data?.state);
  const refreshIntervalMs = isAwaitingApproval
    ? APPROVAL_POLL_MS
    : isLiveRunState(runResp.data?.state)
      ? POLL_MS
      : 0;
  const stagesResp = useListStagesQuery(runId, {
    skip: !runId,
    skipPollingIfUnfocused: true,
    pollingInterval: refreshIntervalMs
  });

  const run = runResp.data;
  const stages = stagesResp.data?.stages ?? [];
  const stageStates = useMemo(
    () => Object.fromEntries(stages.map((s) => [s.stage_id, s.state])),
    [stages]
  );

  const [subProgressByStage, setSubProgressByStage] = useState<
    Record<string, StageSubProgress | undefined>
  >({});
  const [activeBackEdge, setActiveBackEdge] = useState<BackEdgeInfo | null>(null);
  const [attemptByStage, setAttemptByStage] = useState<Record<string, number | undefined>>({});
  const [activityRows, setActivityRows] = useState<LiveActivityRow[]>([]);
  const [currentStep, setCurrentStep] = useState<
    { tool: string; args: string; turn: number } | undefined
  >(undefined);
  const [activityStageId, setActivityStageId] = useState<string | undefined>(undefined);
  const [traceEpoch, setTraceEpoch] = useState(0);

  const [fetchTraces] = useLazyGetRunTracesQuery();
  const [traceHistory, setTraceHistory] = useState<LiveActivityRow[]>([]);
  useEffect(() => {
    if (runId === '') return;
    let cancelled = false;

    void (async () => {
      const all: RunTraceRow[] = [];
      let offset = 0;
      for (;;) {
        const resp = await fetchTraces({ runId, offset, limit: 2000 }).unwrap().catch(() => undefined);
        if (cancelled) return;
        if (!resp) break;
        all.push(...resp.rows);
        if (resp.next_offset === undefined || resp.next_offset <= offset) break;
        offset = resp.next_offset;
      }
      if (cancelled || all.length === 0) return;
      setTraceHistory(foldTraceRows(all));
    })();

    return () => {
      cancelled = true;
    };
  }, [runId, fetchTraces, traceEpoch]);

  useEffect(() => {
    if (traceHistory.length === 0) return;
    setActivityRows((prev) => mergeActivityRows(traceHistory, prev));
  }, [traceHistory]);

  const onFrame = useCallback(
    (frame: PipelineRunEventFrame): void => {
      switch (frame.kind) {
        case 'stage.progress':
          setSubProgressByStage((prev) => ({
            ...prev,
            [frame.stage_id]: {
              progress_pct: frame.progress_pct,
              current_step: frame.step,
              latency_so_far_ms: frame.latency_ms
            }
          }));
          break;
        case 'back_edge.fired':
          setActiveBackEdge({
            from_stage: frame.from_stage,
            to_stage: frame.to_stage,
            attempt_number: frame.attempt_number,
            ...(frame.max_attempts !== undefined ? { max_attempts: frame.max_attempts } : {}),
            fired_at: frame.emitted_at,
            feedback_summary: 'feedback applied — rework in flight'
          });
          setAttemptByStage((prev) => ({
            ...prev,
            [frame.to_stage]: frame.attempt_number
          }));
          setTraceEpoch((n) => n + 1);
          break;
        case 'agent.activity':
          setActivityStageId(frame.stage_id);
          if (frame.phase === 'tool_started') {
            setCurrentStep({ tool: frame.tool_names[0] ?? 'working', args: '', turn: frame.turn });
          } else if (frame.phase === 'tool_args') {
            setCurrentStep((prev) =>
              prev ? { ...prev, args: frame.summary } : { tool: 'working', args: frame.summary, turn: frame.turn }
            );
          } else {
            if (frame.tool_names.length === 0 && frame.summary === '') {
              setCurrentStep(undefined);
              break;
            }
            setActivityRows((prev) => {
              const row: LiveActivityRow = {
                stage_id: frame.stage_id,
                invocation_key: frame.invocation_key,
                turn: frame.turn,
                tool_names: [...frame.tool_names],
                summary: frame.summary,
                ts: frame.emitted_at
              };
              return [...prev.filter((r) => !(r.turn === row.turn && r.invocation_key === row.invocation_key)), row];
            });
            setCurrentStep(undefined);
          }
          break;
        case 'stage.completed':
        case 'stage.failed':
          setTraceEpoch((n) => n + 1);
          void runResp.refetch();
          void stagesResp.refetch();
          dispatch(api.util.invalidateTags(['StageDetail']));
          break;
        case 'stage.started':
        case 'run.state_changed':
          void runResp.refetch();
          void stagesResp.refetch();
          dispatch(api.util.invalidateTags(['StageDetail']));
          break;
        default:
          break;
      }
    },
    [runResp, stagesResp, dispatch]
  );

  const isActive = run?.state === 'running';
  const streamEnabled = isLiveRunState(run?.state);
  const { status: streamStatus, cursor, reconnect } = useEventStream(
    runId || null,
    Boolean(streamEnabled),
    onFrame
  );

  useEffect(() => {
    setSubProgressByStage({});
    setActiveBackEdge(null);
    setAttemptByStage({});
  }, [runId]);

  if (runResp.isLoading) {
    return (
      <div className="p-6 pb-10">
        <div className="bg-panel border border-border rounded-lg p-6">
          <SkeletonGridRow cols={4} />
        </div>
      </div>
    );
  }
  if (runResp.isError || !run) {
    return (
      <div className="p-6 pb-10">
        <ErrorState
          title="Failed to load run"
          body="The pipeline-run record could not be retrieved."
          onRetry={() => void runResp.refetch()}
        />
      </div>
    );
  }

  const pausedFrozenEnd =
    run.state === 'paused_for_approval'
      ? (stages
          .map((s) => s.completed_at)
          .filter((t): t is string => Boolean(t))
          .sort()
          .at(-1) ?? null)
      : null;

  return (
    <div className="p-6 pb-10">
      <header
        className="bg-panel border border-border rounded-lg shadow-xs p-[22px] mb-4"
        data-testid="run-header"
      >
        <div className="flex justify-between items-start gap-4 mb-3.5">
          <div className="flex items-center gap-3">
            <div
              className="font-mono text-[17px] font-semibold text-text"
              data-testid="run-id"
            >
              {run.id}
            </div>
            <DownloadRequirementButton runId={runId} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <Chip state={run.state} />
            {run.signals && run.signals.stalled ? (
              <span
                className="inline-flex items-center gap-1.5 font-mono text-[11.5px] font-medium px-2.5 py-0.5 rounded-full bg-warn/15 text-warn"
                data-testid="header-badge-stalled"
              >
                stalled
              </span>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <Meta label="Started at" value={formatTimestamp(run.started_at)} />
          <Meta
            label="Completed at"
            value={run.finished_at ? formatTimestamp(run.finished_at) : '—'}
          />
          <Meta
            label="Total duration"
            value={formatDuration(run.started_at, run.finished_at, pausedFrozenEnd)}
          />
          <Meta label="Correlation ID" value={run.correlation_id} mono />
        </div>
      </header>

      {run.state === 'paused_for_approval' ? (
        <ApprovalPanel runId={runId} refreshIntervalMs={refreshIntervalMs} />
      ) : null}

      <ProgressTracker
        stages={stages}
        isLive={Boolean(isActive)}
        isAwaitingApproval={isAwaitingApproval}
        streamStatus={streamStatus}
        cursor={cursor}
        onReconnect={reconnect}
        subProgressByStage={subProgressByStage}
        backEdge={activeBackEdge}
        attemptByStage={attemptByStage}
      />

      <LiveActivityPanel
        rows={activityRows}
        current={currentStep}
        status={run.workspace_reclaimed_at ? 'reclaimed' : 'ok'}
        isLive={Boolean(streamEnabled)}
        stageId={activityStageId}
        elapsed={formatDuration(run.started_at, run.finished_at, pausedFrozenEnd)}
        stopControl={
          isLiveRunState(run.state) ? <StopRunButton runId={runId} /> : undefined
        }
        activeStage={values.astage ?? ALL_STAGES}
        onSelectStage={(s) => set({ astage: s === ALL_STAGES ? undefined : s })}
        stageStates={stageStates}
      />

      <nav className="flex gap-0.5 border-b border-border mb-4" role="tablist">
        {TABS.map((t) => (
          <button
            type="button"
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => set({ tab: t.key })}
            className={`px-4 py-2.5 text-[13px] -mb-px border-b-2 ${
              tab === t.key
                ? 'text-text border-text font-medium'
                : 'text-text-muted border-transparent cursor-pointer hover:text-text'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'logs' && (
        <LogsTab
          runId={runId}
          searchQuery={searchQuery}
          onSearch={(q) => set({ q: q || undefined })}
          refreshIntervalMs={refreshIntervalMs}
        />
      )}
      {tab === 'stage_outputs' && (
        <StageOutputsTab
          runId={runId}
          stages={stages}
          selectedStageId={values.stage}
          onSelectStage={(stageId) => set({ stage: stageId })}
          refreshIntervalMs={refreshIntervalMs}
        />
      )}
      {tab === 'cost' && <CostTab runId={runId} refreshIntervalMs={refreshIntervalMs} />}
      {tab === 'errors' && (
        <ErrorsTab
          runId={runId}
          refreshIntervalMs={runRefreshing ? refreshIntervalMs : 0}
          panel={(values.panel as 'failure' | 'stall' | undefined) ?? undefined}
          onPanelChange={(p) => set({ panel: p })}
        />
      )}
    </div>
  );
}

function Meta({
  label,
  value,
  mono = false
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="font-mono text-[9.5px] text-text-muted uppercase tracking-[0.1em] mb-1">
        {label}
      </div>
      <div
        className={`text-[13px] text-text tabular-nums ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

function LogsTab({
  runId,
  searchQuery,
  onSearch,
  refreshIntervalMs
}: {
  runId: string;
  searchQuery: string;
  onSearch: (q: string) => void;
  refreshIntervalMs: number;
}): JSX.Element {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (localQuery !== searchQuery) onSearch(localQuery);
    }, 250);
    return () => clearTimeout(t);
  }, [localQuery, searchQuery, onSearch]);

  const eventsResp = useSearchEventsQuery(
    {
      subject: runId,
      per_page: 5000
    },
    { pollingInterval: refreshIntervalMs, skipPollingIfUnfocused: true }
  );

  return (
    <section
      className="bg-panel border border-border rounded-lg overflow-hidden"
      data-testid="logs-tab"
    >
      <div className="px-4 py-2.5 border-b border-border flex gap-3 items-center">
        <div className="flex-1 max-w-[400px] relative">
          <span
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[13px]"
            aria-hidden="true"
          >
            ⌕
          </span>
          <input
            type="search"
            autoComplete="off-log-search"
            className="w-full h-[30px] pl-8 pr-8 bg-panel-2 border border-border rounded-md text-text text-[12.5px] focus:border-accent outline-none"
            placeholder="Search log text (matches highlighted inline)"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            aria-label="Search log events"
          />
        </div>
        <div className="text-[11.5px] text-text-muted ml-auto">
          {eventsResp.data
            ? `Virtualised list · ${eventsResp.data.events.length} events`
            : ''}
        </div>
      </div>

      {eventsResp.isLoading ? (
        <div className="p-3">
          {Array.from({ length: 10 }, (_, i) => (
            <SkeletonGridRow key={i} cols={5} />
          ))}
        </div>
      ) : eventsResp.isError ? (
        <ErrorState
          title="Couldn't load logs"
          body="The log events couldn't be loaded right now. The run header and stage timeline above are still current."
          onRetry={() => void eventsResp.refetch()}
        />
      ) : !eventsResp.data || eventsResp.data.events.length === 0 ? (
        <EmptyState
          title="No log events for this run yet"
          body="Events will appear here as the pipeline progresses through each stage."
        />
      ) : (
        <VirtualLogList events={eventsResp.data.events} searchQuery={localQuery} />
      )}
    </section>
  );
}
